import { Component, Element, Fragment, Prop, State, h } from '@stencil/core';

import { isModificationQuestionsAnswered, removeModificationsAnswers } from '../../../actions/actions.answers';
import { trackModificationsSelection } from '../../../tracking/tracking.keysModifications';
import { isAnsweredCategory } from '../../../utils/utils.vehicle';

@Component({
  tag: 'vehicle-modifications',
  styleUrl: 'vehicle-modifications.css',
})
export class VehicleModifications {
  @Element() el: HTMLElement;

  @Prop() questionsExhaust = [];
  @Prop() questionsPerformance = [];
  @Prop() questionsStereo = [];
  @Prop() questionsSuspension = [];
  @Prop() questionsWheelsTires = [];
  @Prop() questionsToggleModifications = [];

  @Prop() answers = {};

  @Prop() showModificationRemovalPrompt: boolean = false;
  @Prop() showModificationsQuestion: boolean = false;
  @Prop() showModificationsQuestionGroup: boolean = false;
  @Prop() showFormValidation: boolean = false;
  @Prop() hasError: boolean = false;
  @Prop() formErrors: Set<string> = new Set();
  @Prop() dataTestId: string = '';
  @Prop() modalPositionOffsetTop: string = '';

  @State() activeQuestionSet = 'questionsExhaust';

  getModalHeader() {
    return this[this.activeQuestionSet]?.[0]?.itemHeader ?? 'Questions about your car';
  }

  getModalSubHeader() {
    return this[this.activeQuestionSet]?.[0]?.itemDescription ?? 'Select the type(s) of wear or damage you have.';
  }

  openModal() {
    const modal = this.el.querySelector('ico-modal#modification-question-modal') as HTMLIcoModalElement;
    if (this.el && this.el && modal) {
      modal.open();
    }
  }

  closeModal(hasKeepMods = false) {
    const modal = this.el.querySelector('ico-modal#modification-question-modal') as HTMLIcoModalElement;
    modal.close(hasKeepMods);
    this.activeQuestionSet = '';
  }

  hasAnsweredQuestions(questionSet = '') {
    const questions = this[questionSet];
    return isAnsweredCategory(questions, this.answers);
  }

  onQuestionSetClick(questionSet = '') {
    this.activeQuestionSet = questionSet;
    this.openModal();
    trackModificationsSelection(questionSet);
  }

  onModificationQuestionChange() {
    if (isModificationQuestionsAnswered() && this.showModificationsQuestionGroup && !this.showModificationRemovalPrompt) {
      this.openModal();
    }
  }

  renderToggleQuestion() {
    if (this.showModificationsQuestion) {
      return this.questionsToggleModifications.map((q) => (
        <div class="mt-8">
          <question-proxy
            question={q}
            answers={this.answers}
            showXLText={true}
            isDirty={this.showFormValidation}
            hasError={this.formErrors.has(q.questionId)}
            errorMessage="Answer Required"
            whenChanged={() => {
              this.onModificationQuestionChange();
            }}
            data-testId={`${this.dataTestId}Proxy`}
            vehicleKeysModButtonStyle={true}
          />
        </div>
      ));
    }
  }

  onKeepModifications() {
    this.closeModal(true);
  }

  onRemoveModifications() {
    removeModificationsAnswers();
    this.closeModal();
  }

  render() {
    const modificationButtons = [
      { icon: 'stereo', label: 'Stereo', questionSet: 'questionsStereo', testId: 'modificationQuestionGroupStereoButton' },
      { icon: 'wheels', label: 'Wheels & Tires', questionSet: 'questionsWheelsTires', testId: 'modificationQuestionGroupWheelsTiresButton' },
      { icon: 'suspension', label: 'Suspension', questionSet: 'questionsSuspension', testId: 'modificationQuestionGroupSuspensionButton' },
      { icon: 'performance', label: 'Performance Modifications', questionSet: 'questionsPerformance', testId: 'modificationQuestionGroupPerformanceModificationsButton' },
      { icon: 'exhaust', label: 'Exhaust', questionSet: 'questionsExhaust', testId: 'modificationQuestionGroupExhaustButton' },
    ];
    const displayModificationQuestionModal = this.showModificationsQuestion && !this.showModificationRemovalPrompt;
    const displayQuestionRemovalPrompt = !this.showModificationsQuestionGroup && this.showModificationRemovalPrompt;

    const activeQuestions = this[this.activeQuestionSet];

    return (
      <div>
        {this.renderToggleQuestion()}
        {this.showModificationsQuestionGroup && (
          <div class="w-full mt-8" data-testId="modificationQuestionGroup">
            <div class="ico-components-heading-large mb-2">Do You Have Any of These Modifications?</div>
            <div class="text-sm mb-4">Modifications means anything that was added after the original purchase, but it can also be upgrades or changes from the dealer.</div>
            <ul class="list-none flex flex-wrap w-full p-0 gap-4 mb-2">
              {modificationButtons.map((mod) => {
                return (
                  <li class="text-center">
                    <input-square-button
                      label={mod.label}
                      size="medium"
                      onClick={() => {
                        this.onQuestionSetClick(mod.questionSet);
                      }}
                      isActive={this.hasAnsweredQuestions(mod.questionSet)}
                      data-testId={mod.testId}>
                      <svg-icon icon={mod.icon} width="3rem" />
                    </input-square-button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        <ico-modal
          displayX={!displayModificationQuestionModal}
          closeModalKeepModsSelections={!displayModificationQuestionModal}
          dataTestId="modificationQuestionModal"
          id="modification-question-modal"
          autoOverflow={false}
          modalPositionOffsetTop={this.modalPositionOffsetTop}>
          {displayModificationQuestionModal && (
            <div>
              <question-modal-header slot="header" header={this.getModalHeader()} subHeader={this.getModalSubHeader()} showExample={false}></question-modal-header>
              <div slot="main" class="max-h-56vh md:max-h-64 overflow-auto my-4">
                {!!activeQuestions && Array.isArray(activeQuestions) && (
                  <question-set
                    questions={activeQuestions}
                    key={this.activeQuestionSet}
                    answers={this.answers}
                    displaySubHeading={false}
                    dataTestId={this.activeQuestionSet}
                    data-testId={this.activeQuestionSet}
                  />
                )}
              </div>
              <question-modal-footer slot="footer" whenDoneClicked={() => this.closeModal()}></question-modal-footer>
            </div>
          )}

          {displayQuestionRemovalPrompt && (
            <Fragment>
              <h2 slot="header" class="ico-components-heading max-w-lg">
                Are You Sure You Want to Remove Modification(s)?
              </h2>
              <div slot="main" class="max-w-lg mb-2">
                You previously selected one or more modification(s). By selecting "No" you will be removing all your previous modification selections.
              </div>

              <div slot="footer" class="flex justify-between">
                <ico-button label="Keep" buttonSize="large" whenClicked={() => this.onKeepModifications()} buttonStyle="tertiary" />
                <ico-button label="Remove" buttonSize="large" whenClicked={() => this.onRemoveModifications()} />
              </div>
            </Fragment>
          )}
        </ico-modal>
      </div>
    );
  }
}
